// =====================================================================================
// INVRT Radar - web console - the contact dossier page
//
// The drawer's dossier as a real page at /contacts/:id (story C4), so a lead's record can
// be bookmarked, shared inside the team and returned to. The page and the drawer render
// the SAME extracted body (ContactDossierBody in ContactDrawer.tsx) from the SAME read
// (useContactDetail), so the two surfaces can never disagree about what the wire said; the
// drawer stays the in-list quick view and links here ("Open as a page").
//
// The wire is GET /contacts/:id (docs/API-CONTRACTS-C1.md §5): the C2 card rides
// additively under `card` and is deliberately not re-rendered - every field it carries is
// already on the Contact this page shows. The C3 engagement facts surface in the badge
// row and the Record section, exactly as in the drawer. An unknown or foreign id gets the
// server's contact_not_found refusal through the shared ErrorState, the same copy the
// drawer shows.
// =====================================================================================

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSites } from "@/hooks/useApi";
import { ErrorState, LoadingState } from "@/components/States";
import { ContactBadges, ContactDossierBody, useContactDetail } from "./ContactDrawer";
import { PersonJourneyTimeline } from "./PersonJourneyTimeline";
import { PushToTargetDialog } from "./PushToTargetDialog";
import { SendContactsDialog } from "./SendContactsDialog";
import { contactDisplayName, useIsOrgAdmin } from "./useContacts";

export function ContactDossierPage() {
  const { contactId = "" } = useParams<{ contactId: string }>();
  const { contact, record, pulseSubject } = useContactDetail(contactId);
  const sites = useSites();
  const isAdmin = useIsOrgAdmin();
  const [sending, setSending] = useState(false);
  const [pushing, setPushing] = useState(false);

  const name = record ? contactDisplayName(record) : "Contact";
  const siteDomain =
    record?.site_id
      ? (sites.data?.sites.find((site) => site.id === record.site_id)?.domain ?? record.site_id)
      : null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link
          to="/contacts"
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          All contacts
        </Link>
        <h1 className="mt-2 text-xl font-semibold text-ink">{name}</h1>
        {record ? (
          <div className="mt-2">
            <ContactBadges record={record} />
          </div>
        ) : null}
      </div>

      {contact.isLoading && !record ? <LoadingState label="Loading this contact…" /> : null}
      {contact.isError ? (
        <ErrorState error={contact.error} onRetry={() => void contact.refetch()} />
      ) : null}

      {record ? (
        <ContactDossierBody
          record={record}
          siteDomain={siteDomain}
          isAdmin={isAdmin}
          isPlaceholder={contact.isPlaceholderData}
          pulseLoading={contact.isFetching}
          pulseSubject={pulseSubject}
          onRequestSend={() => setSending(true)}
          onRequestPush={() => setPushing(true)}
          headingLevel={2}
          /* Story O2 (Mark ruling 7): the flat "Pages viewed" list is the drawer's read-out;
             on the page the journey timeline below is the one rendering of the page views.
             The campaign attribution moves to the Record section, so nothing is lost. */
          showVisitSection={false}
        />
      ) : null}

      {/* Story C8: the person journey timeline (the person-level counterpart of
          CompanyJourneySection) renders inside this labelled container - page views only,
          with the honest empty state when nothing was recorded. Story O2: since the body's
          flat visit list stays on the drawer only, this timeline is the page's single
          rendering of the page views. */}
      {record ? (
        <section
          id="c8-person-journey-slot"
          data-testid="c8-person-journey-slot"
          aria-label="Person journey"
          className="flex flex-col gap-3"
        >
          <PersonJourneyTimeline pages={record.pages} />
        </section>
      ) : null}

      <SendContactsDialog
        open={sending}
        contactIds={[contactId]}
        onClose={() => setSending(false)}
      />
      <PushToTargetDialog
        open={pushing}
        contactIds={[contactId]}
        onClose={() => setPushing(false)}
      />
    </div>
  );
}
